import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlarmClock, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useMyDailyBoard } from '../../hooks/useDailyStatus';
import { useAuthStore } from '../../store/useAuthStore';
import { usePermissions } from '../../hooks/usePermissions';
import { Popover } from '../tasks/linear/Popover';

const DISMISS_KEY = 'ccc.daily-status-dismissed';
const SNOOZE_KEY = 'ccc.daily-status-snooze-until';

// The same red as the personal reminders strip: this is the same kind of
// "you are about to miss something", so it must read as one system.
const PALETTE = {
  bar: 'border-red-500/40 bg-red-500/10',
  text: 'text-red-600',
  dot: 'bg-red-500',
  button: 'text-red-600 hover:bg-red-500/15 hover:text-red-700',
} as const;

/** "HH:MM" and the ISO weekday as a clock on the wall in `timeZone` shows now. */
function wallClockNow(timeZone: string): { hhmm: string; weekday: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
  }).formatToParts(new Date());
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return { hhmm: `${get('hour')}:${get('minute')}`, weekday: days.indexOf(get('weekday')) };
}

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Not remembered across reloads; the component state still hides it now.
  }
}

/**
 * The end-of-day check-in reminder — drawn exactly like a firing personal
 * reminder (red strip, dot, title, due chip, Amână / X), from the configured
 * reminder time until midnight on workdays, while the person still has tasks
 * without a status. The title opens their Daily Summary for that day, where
 * the overdue and due-today tasks wait to be answered.
 */
export function DailyStatusBar() {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const userId = useAuthStore((s) => s.user?.id);
  const { can } = usePermissions();
  const board = useMyDailyBoard(undefined, Boolean(userId) && can('dailySummary', 'canView'));
  const [dismissedDay, setDismissedDay] = useState<string | null>(() => readStorage(DISMISS_KEY));
  const [snoozedUntil, setSnoozedUntil] = useState<number>(() =>
    Number(readStorage(SNOOZE_KEY) ?? 0),
  );

  const data = board.data;
  if (!data || !data.schedule.enabled || data.pending === 0) return null;
  // A firing is "day + when it went out", so a push from Settings after the
  // person closed the strip brings it back.
  const firing = `${data.day}@${data.schedule.reminderFiredAt ?? 'clock'}`;
  if (dismissedDay === firing) return null;
  if (snoozedUntil > Date.now()) return null;
  // The board itself is where you fix it — no nagging on top of it.
  if (pathname.startsWith('/daily-summary')) return null;

  // The server marker (17:30 run or an admin pushing it) shows the strip
  // whatever the clock says; otherwise fall back to the wall-clock rule.
  if (!data.schedule.reminderFiredAt) {
    const clock = wallClockNow(data.schedule.timeZone);
    if (clock.weekday === 0 || clock.weekday === 6) return null;
    if (clock.hhmm < data.schedule.reminderTime) return null;
  }

  const target = `/daily-summary?userId=${encodeURIComponent(userId ?? '')}&date=${data.day}`;
  const btn = `inline-flex items-center gap-1 rounded px-1.5 py-1 text-[12px] disabled:opacity-50 ${PALETTE.button}`;

  function dismiss() {
    if (!data) return;
    writeStorage(DISMISS_KEY, firing);
    setDismissedDay(firing);
  }

  function snooze(minutes: number) {
    const until = Date.now() + minutes * 60_000;
    writeStorage(SNOOZE_KEY, String(until));
    setSnoozedUntil(until);
  }

  return (
    <div className={`flex items-center gap-2 border-b px-3 py-1.5 ${PALETTE.bar}`}>
      <span className={`h-2 w-2 shrink-0 animate-pulse rounded-full ${PALETTE.dot}`} />
      <Link
        to={target}
        className="min-w-0 flex-1 truncate text-[13px] text-gray-800 hover:underline"
      >
        <span className="font-medium">{t('dailyStatus.bar.title')}</span>
        <span className="ml-1.5 opacity-60">
          {t('dailyStatus.bar.pending', { count: data.pending })}
        </span>
      </Link>
      <span
        className={`hidden shrink-0 text-[12px] font-semibold tabular-nums sm:inline ${PALETTE.text}`}
      >
        {t('reminders.today')}, {data.schedule.reminderTime}
      </span>
      <div className="flex shrink-0 items-center gap-0.5">
        <Link to={target} className={btn} title={t('dailyStatus.bar.cta')}>
          {t('dailyStatus.bar.cta')}
        </Link>
        <Popover
          width={180}
          align="end"
          trigger={({ open, toggle, ref }) => (
            <button
              ref={ref}
              type="button"
              onClick={toggle}
              aria-haspopup="menu"
              aria-expanded={open}
              title={t('reminders.snooze')}
              className={btn}
            >
              <AlarmClock size={13} />
              <span>{t('reminders.snooze')}</span>
            </button>
          )}
        >
          {(close) => (
            <div className="py-1">
              {[
                { label: t('reminders.snooze10m'), minutes: 10 },
                { label: t('reminders.snooze1h'), minutes: 60 },
              ].map((option) => (
                <button
                  key={option.minutes}
                  type="button"
                  onClick={() => {
                    snooze(option.minutes);
                    close();
                  }}
                  className="block w-full px-3 py-1.5 text-left text-[13px] text-foreground hover:bg-accent"
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </Popover>
        <button
          type="button"
          onClick={dismiss}
          title={t('dailyStatus.bar.dismiss')}
          aria-label={t('dailyStatus.bar.dismiss')}
          className={btn}
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}
